import {
  AfterViewChecked,
  AfterViewInit,
  Component,
  ElementRef,
  HostListener, Input,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { AsyncPipe, NgClass, NgOptimizedImage, NgTemplateOutlet } from "@angular/common";
import { ChatbotService } from "../../shared/service/chatbot/chatbot.service";
import { TranslocoModule } from "@ngneat/transloco";
import { ChatFeaturedShopComponent } from "./chat-featured-shop/chat-featured-shop.component";
import { AnalyticsEventCategory, sendEvent } from "../../shared/analytics/analytics";
import { ContentResponseType, ResultType } from './models/content-response-type';
import { ShopRecommendations } from "./models/shop-recommendations";
import { interval, Subject, take } from "rxjs";
import { INTERACTION_TYPE_LABELS, InteractionType } from "./models/interaction-type";
import { IsActionablePipe } from "./util/actionable-pipe";
import { ToActionablePipe } from "./util/to-actionable-pipe";
import { ReferrerTranslatePipe } from "./util/referrer-translate";
import { ChatbotContextService } from "../../shared/service/chatbot/chatbot-context.service";

@Component({
    selector: 'app-chatbot',
    templateUrl: './chatbot.component.html',
    styleUrl: './chatbot.component.scss',
    imports: [
        FormsModule,
        NgClass,
        NgOptimizedImage,
        ReactiveFormsModule,
        TranslocoModule,
        NgTemplateOutlet,
        ChatFeaturedShopComponent,
        IsActionablePipe,
        ToActionablePipe,
        ReferrerTranslatePipe,
        AsyncPipe,
        ReferrerTranslatePipe
    ]
})
export class ChatbotComponent implements OnInit, AfterViewChecked, OnDestroy {

  constructor(private chatbotService: ChatbotService,
              private chatbotContextService: ChatbotContextService,
              private elRef: ElementRef) {
  }

  protected readonly INTERACTION_TYPE_LABELS = INTERACTION_TYPE_LABELS;

  modalHidden: boolean = true;
  showIsHelpful: boolean = false;
  isLoading: boolean = false;
  showTooltip: boolean = false;
  needScroll: boolean = false;

  @ViewChild('messageBodyEnd') private messageBodyEnd!: ElementRef;
  @Input() shouldDisplay = false;

  messages: ContentResponseType[] = [];

  form = new FormGroup({
    userMessage: new FormControl<string>(''),
  })

  destroyEvent$ = new Subject<void>();

  ngOnInit(): void {
    this.handleTooltip();
  }

  ngAfterViewChecked(): void {
    if (this.needScroll) {
      this.needScroll = false;
      this.scrollToBottom();
    }
  }

  ngOnDestroy(): void {
    this.destroyEvent$.next();
    this.destroyEvent$.complete();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const modalElement = this.elRef.nativeElement.querySelector('#chatbot-modal');
    if (modalElement && !modalElement.contains(event.target as Node)) {
      this.modalHidden = true;
    }
  }

  sendChatbotOpenEvent(): void {
    sendEvent(AnalyticsEventCategory.CHATBOT_OPEN, window.location.pathname)
  }

  sendMessage() {
    this.submitChatRequest();
  }

  private handleTooltip() {
    this.showTooltip = true;
    interval(10000).pipe(take(1)).subscribe(() => {
      this.showTooltip = false;
    });
  }

  private createUserMessage(message: string): ContentResponseType {
    return {
      message: message,
      resultType: ResultType.USER_TEXT,
    } as ContentResponseType;
  }

  public submitChatRequest(): void {
    sendEvent(AnalyticsEventCategory.CHATBOT_USER_SEND, this.messages?.length ?? 0)

    this.showIsHelpful = false;
    const messageText = this.form.controls.userMessage.value?.trim();
    if (!messageText) return;

    this.isLoading = true;

    const userMessage = this.createUserMessage(messageText);
    this.messages.push(userMessage);
    this.form.reset();

    this.chatbotService.postGetChat(messageText, this.chatbotContextService.getSessionUserId()).subscribe({
      next: (response) => {
        this.isLoading = false;
        response.content.forEach(message => {
          this.messages.push(message);
        })

        if (response.content.some(message => message?.resultType === ResultType.SHOP_RECOMMENDATIONS)) {
          sendEvent(AnalyticsEventCategory.CHATBOT_AI_RECOMMENDATION, this.messages?.length ?? 0)
          this.chatbotContextService.updateRecommendedList(response);
          this.showIsHelpful = true;
        } else {
          sendEvent(AnalyticsEventCategory.CHATBOT_AI_TEXT, this.messages?.length ?? 0)
        }

        this.needScroll = true;
      },
      error: (error) => {
        this.isLoading = false;
      }
    })
  }

  public submitButtonInteractRequest(interactionType: InteractionType) {

    if (this.isLoading) {
      return;
    }

    let offset = null;
    this.showIsHelpful = false;
    this.isLoading = true;

    if (interactionType === InteractionType.SHOW_POPULAR_RECOMMENDATIONS) {
      offset = this.chatbotContextService.getPopularRestaurantOffset();
    }

    sendEvent(AnalyticsEventCategory.CHATBOT_BUTTON_INTERACT, interactionType);
    this.chatbotService.postGetButtonInteract(interactionType, this.chatbotContextService.getSessionUserId(), offset).subscribe({
      next: (response) => {
        this.isLoading = false;
        response.content.forEach(message => {
          this.messages.push(message);
        })

        this.showIsHelpful = true;
        this.needScroll = true;
        this.isLoading = false;
      },
      error: (error) => {
        this.isLoading = false;
      }
    })
  }

  clickIsHelpful(isHelpful: boolean) {
    let recommendationResponseList = this.messages.filter(message => message?.resultType === ResultType.SHOP_RECOMMENDATIONS);
    let eventMessage = this.messages.length + '';

    if (recommendationResponseList && recommendationResponseList[0]) {
      let recommendedShops = recommendationResponseList[0] as ShopRecommendations;

      eventMessage = recommendedShops.shops.map(message => message?.shopNumber).join(',')
    }

    if (isHelpful) {
      sendEvent(AnalyticsEventCategory.CHATBOT_THUMBS_UP, eventMessage)
    } else {
      sendEvent(AnalyticsEventCategory.CHATBOT_THUMBS_DOWN, eventMessage)
    }
    this.showIsHelpful = false;
  }

  scrollToBottom(): void {
    this.messageBodyEnd.nativeElement.scrollIntoView({behavior: 'smooth'});
  }

}
